// ─── buildConsultationBriefing.js ──────────────────────────────────────────
// Agrégation PURE des données de la période « depuis la dernière consultation »
// pour préparer un rendez-vous. Aucune I/O, aucun appel réseau : reçoit les
// ressentis + pesées déjà chargés côté Suivi et en tire un objet structuré
// (période, poids, tendances par axe, commentaires). Testable isolément.
//
// GARDE-FOUS
// - Aucune décision clinique ici : on COMPTE et on RÉSUME ce que la cliente a
//   saisi, on n'interprète pas. Le mapping des libellés est purement cosmétique
//   (miroir de FEEDBACK_SUMMARY_DIMS dans ClientJourneyPage.jsx).
// - Défaut prudent : si un axe n'a aucune valeur sur la fenêtre, il est omis
//   (pas de tendance inventée). Delta poids seulement si ≥ 2 pesées.
//
// Forme d'un ressenti (JSONB app cliente) :
//   { date, digestion, fatigue, energie, faim, note, weight_kg }

// Miroir de FEEDBACK_SUMMARY_DIMS (ClientJourneyPage.jsx). Duplication assumée
// et volontaire : ce module doit rester pur et testable sans importer le gros
// composant React. L'ordre définit l'ordre d'affichage des tendances.
export const BRIEFING_AXES = [
  { key: 'digestion', label: 'Digestion', values: { better: 'Mieux', same: 'Pareil', worse: 'Pire' } },
  { key: 'fatigue',   label: 'Fatigue',   values: { better: 'Mieux', same: 'Pareil', worse: 'Pire' } },
  { key: 'energie',   label: 'Énergie',   values: { good: 'Bonne', ok: 'OK', low: 'Basse' } },
  { key: 'faim',      label: 'Faim',      values: { low: 'Pas assez', ok: 'OK', high: 'Trop' } },
];

const toTime = (v) => {
  const t = new Date(v).getTime();
  return Number.isFinite(t) ? t : null;
};

/**
 * Formate un libellé de période lisible (fr-CH) : « du 3 juin au 8 juillet ».
 * @param {string} sinceISO
 * @param {string} nowISO
 * @returns {string}
 */
export function formatPeriodLabel(sinceISO, nowISO) {
  const start = sinceISO ? new Date(sinceISO) : null;
  const end = nowISO ? new Date(nowISO) : null;
  const fmt = (d) =>
    d && !Number.isNaN(d.getTime())
      ? d.toLocaleDateString('fr-CH', { day: 'numeric', month: 'long' })
      : null;
  const s = fmt(start);
  const e = fmt(end);
  if (s && e) return `du ${s} au ${e}`;
  if (e) return `jusqu'au ${e}`;
  return 'période récente';
}

/**
 * Construit l'objet de données du briefing sur la fenêtre [sinceISO, nowISO].
 * PUR. Filtre les ressentis et pesées à la fenêtre, calcule la tendance
 * dominante par axe (valeur la plus fréquente + son compte), collecte les
 * commentaires libres datés, et l'évolution du poids (début → fin, delta).
 *
 * @param {object} input
 * @param {Array}  [input.feedbacks]      - ressentis { date|created_at, digestion, fatigue, energie, faim, note, weight_kg }
 * @param {Array}  [input.weightEntries]  - pesées { date, weight_kg } (déjà dérivées côté Suivi) — optionnel, sinon dérivées des feedbacks
 * @param {string} input.sinceISO         - borne basse (incluse)
 * @param {string} input.nowISO           - borne haute (incluse)
 * @returns {{ periodLabel:string, weight:(null|{start:number,end:number,deltaKg:number,points:Array}), trends:Array<{axis:string,label:string,dominant:string,count:number,total:number}>, comments:Array<{date:string,text:string}> }}
 */
export function buildBriefingData({ feedbacks = [], weightEntries = [], sinceISO, nowISO } = {}) {
  const fbs = Array.isArray(feedbacks) ? feedbacks : [];
  const since = toTime(sinceISO);
  const now = toTime(nowISO) ?? Date.now();
  const lo = since ?? -Infinity;

  const inWindow = (v) => {
    const t = toTime(v);
    return t !== null && t >= lo && t <= now;
  };

  // Ressentis de la fenêtre (chaque ressenti porte sa date sur `date` ou `created_at`).
  const windowFbs = fbs.filter((f) => f && inWindow(f.date || f.created_at));

  // ─── Tendances par axe ─────────────────────────────────────────────────
  const trends = BRIEFING_AXES.map((axis) => {
    const counts = {};
    let total = 0;
    for (const f of windowFbs) {
      const v = f[axis.key];
      if (!v) continue;
      counts[v] = (counts[v] || 0) + 1;
      total += 1;
    }
    const entries = Object.entries(counts).sort((a, b) => b[1] - a[1]);
    if (entries.length === 0) return null;
    const count = entries[0][1];
    const tied = entries.filter(([, valueCount]) => valueCount === count);
    const tiedValues = tied.map(([value]) => axis.values[value] || value);
    return {
      axis: axis.key,
      label: axis.label,
      dominant: tied.length === 1 ? tiedValues[0] : null,
      tiedValues: tied.length > 1 ? tiedValues : [],
      count,
      total,
    };
  }).filter(Boolean);

  // ─── Commentaires libres datés ─────────────────────────────────────────
  const comments = windowFbs
    .filter((f) => typeof f.note === 'string' && f.note.trim())
    .map((f) => ({ date: f.date || f.created_at, text: f.note.trim() }))
    .sort((a, b) => (toTime(a.date) ?? 0) - (toTime(b.date) ?? 0));

  // ─── Évolution du poids ────────────────────────────────────────────────
  // Source : weightEntries fournies si présentes, sinon dérivées des ressentis.
  // On borne à la fenêtre et on trie chronologiquement (ancien → récent).
  const rawWeights = (Array.isArray(weightEntries) && weightEntries.length
    ? weightEntries
    : fbs
        .filter((f) => f && typeof f.weight_kg === 'number')
        .map((f) => ({ date: f.date || f.created_at, weight_kg: Number(f.weight_kg) })))
    .filter((w) => w && typeof w.weight_kg === 'number' && inWindow(w.date))
    .sort((a, b) => (toTime(a.date) ?? 0) - (toTime(b.date) ?? 0));

  let weight = null;
  if (rawWeights.length) {
    const start = rawWeights[0].weight_kg;
    const end = rawWeights[rawWeights.length - 1].weight_kg;
    weight = {
      start,
      end,
      deltaKg: rawWeights.length > 1 ? Math.round((end - start) * 10) / 10 : null,
      points: rawWeights.map((w) => ({ date: w.date, weight_kg: w.weight_kg })),
    };
  }

  return {
    periodLabel: formatPeriodLabel(sinceISO, nowISO),
    weight,
    trends,
    comments,
  };
}

/**
 * Détermine la borne basse de la période « depuis la dernière consultation ».
 * PUR. Priorité : dernière consultation loggée > publication du plan actuel /
 * packStartedAt > fallback 35 jours. Borne haute = now (fournie par l'appelant).
 *
 * @param {object} input
 * @param {Array}  [input.consultationsLog] - [{ date, ... }] (append-only, croissant)
 * @param {string} [input.planPublishedAt]  - date de publication du plan actif
 * @param {string} [input.packStartedAt]    - date de démarrage du pack
 * @param {string|number|Date} [input.now]  - borne haute (défaut Date.now())
 * @returns {string} ISO de la borne basse
 */
export function resolveBriefingSince({ consultationsLog = [], planPublishedAt, packStartedAt, now = Date.now() } = {}) {
  const nowTime = toTime(now) ?? Date.now();

  const log = Array.isArray(consultationsLog) ? consultationsLog : [];
  // Dernière consultation dont la date est antérieure à maintenant.
  const consultDates = log
    .map((c) => (c && c.date ? toTime(c.date) : null))
    .filter((t) => t !== null && t <= nowTime)
    .sort((a, b) => a - b);
  const lastConsult = consultDates.length ? consultDates[consultDates.length - 1] : null;
  if (lastConsult !== null) return new Date(lastConsult).toISOString();

  const plan = toTime(planPublishedAt);
  if (plan !== null && plan <= nowTime) return new Date(plan).toISOString();

  const pack = toTime(packStartedAt);
  if (pack !== null && pack <= nowTime) return new Date(pack).toISOString();

  // Fallback ultime : 35 derniers jours.
  return new Date(nowTime - 35 * 86400000).toISOString();
}
